
export default function CareGuide() {
  return (
    <section id="care" className="py-16 md:py-32 px-4 md:px-8 lg:px-20 bg-[#F8F6F0]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center lg:flex lg:justify-between lg:items-start lg:text-left mb-12 md:mb-16">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-gray-900 lg:max-w-xl leading-tight mb-4 lg:mb-0">
            Complete Care Guide
          </h2>
          <p className="text-base md:text-lg text-gray-600 lg:max-w-md">
            Essential information for keeping your Leachianus healthy and thriving in captivity
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-6">
          {/* Enclosure Requirements */}
          <div className="lg:col-span-3 bg-white rounded-2xl p-6 md:p-8 shadow-md">
            <div className="mb-6">
              <img
                src="https://readdy.ai/api/search-image?query=vertical%20terrarium%20enclosure%20setup%20for%20arboreal%20gecko%20with%20branches%20plants%20and%20climbing%20surfaces%20professional%20product%20photography%20simple%20clean%20white%20background&width=400&height=500&seq=care-enclosure-006&orientation=portrait"
                alt="Terrarium Setup"
                className="w-full h-48 md:h-64 object-cover rounded-xl"
              />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Enclosure Requirements</h3>
            <ul className="space-y-3 md:space-y-4">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#B8E986] text-lg md:text-xl mt-1 flex-shrink-0"></i>
                <span className="text-gray-700 text-sm md:text-base"><strong>Minimum Size:</strong> 24" x 18" x 36" for adults</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#B8E986] text-lg md:text-xl mt-1 flex-shrink-0"></i>
                <span className="text-gray-700 text-sm md:text-base"><strong>Vertical Space:</strong> Height is crucial for arboreal species</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#B8E986] text-lg md:text-xl mt-1 flex-shrink-0"></i>
                <span className="text-gray-700 text-sm md:text-base"><strong>Substrate:</strong> Coconut fiber or cypress mulch</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#B8E986] text-lg md:text-xl mt-1 flex-shrink-0"></i>
                <span className="text-gray-700 text-sm md:text-base"><strong>Furnishings:</strong> Multiple branches and hiding spots</span>
              </li>
            </ul>
          </div>

          {/* Temperature */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 md:p-8 shadow-md flex flex-col items-center justify-center text-center">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Temperature</h3>
            <div className="mb-4 md:mb-6">
              <i className="ri-temp-hot-line text-5xl md:text-6xl text-orange-500"></i>
            </div>
            <div className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">72-78°F</div>
            <p className="text-gray-600 font-medium mb-2">Optimal Range</p>
            <p className="text-xs md:text-sm text-gray-500">Maintain consistent temperatures with slight nighttime drop</p>
          </div>

          {/* Humidity */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 md:p-8 shadow-md flex flex-col items-center justify-center text-center">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Humidity</h3>
            <div className="mb-4 md:mb-6">
              <i className="ri-drop-line text-5xl md:text-6xl text-blue-500"></i>
            </div>
            <div className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">60-80%</div>
            <p className="text-gray-600 font-medium mb-2">Daily Misting</p>
            <p className="text-xs md:text-sm text-gray-500">Mist enclosure twice daily to maintain humidity levels</p>
          </div>

          {/* Feeding */}
          <div className="md:col-span-2 lg:col-span-7 bg-white rounded-2xl p-6 md:p-8 shadow-md">
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
              <div className="text-center md:text-left">
                <div className="mb-4 md:mb-6">
                  <i className="ri-restaurant-line text-5xl md:text-6xl text-green-600"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-4">Feeding</h3>
                <div className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">2-3x Weekly</div>
                <p className="text-gray-600 font-medium">Varied Diet</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 flex-1 w-full">
                <div className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#B8E986]"></i>
                  <span className="text-sm md:text-base text-gray-700">Crickets and roaches</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#B8E986]"></i>
                  <span className="text-sm md:text-base text-gray-700">Fruit-based gecko diet</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#B8E986]"></i>
                  <span className="text-sm md:text-base text-gray-700">Calcium supplementation</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 md:mt-12 bg-emerald-100 rounded-2xl p-6 md:p-8 border-l-4 border-emerald-600">
          <div className="flex items-start gap-4">
            <i className="ri-information-line text-2xl md:text-3xl text-emerald-700 flex-shrink-0"></i>
            <div>
              <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Important Care Note</h4>
              <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                Leachianus geckos are best suited for experienced reptile keepers. They require consistent care, proper handling techniques, and a well-maintained environment. Always research thoroughly and consider adopting from reputable breeders who can provide health history and care guidance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
